import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SearchDestination from '../search-destination/SearchDestination';
import { Link, useLocation } from 'react-router-dom';
import ROUTES_ENUMS from 'routes/url.enum';
import { LuUserRound } from 'react-icons/lu';
import { RiFileList2Line } from 'react-icons/ri';
import { FaRegQuestionCircle, FaBars, FaTimes } from 'react-icons/fa';
import { LuLogOut } from 'react-icons/lu';
import { FaRegUserCircle } from 'react-icons/fa';
import { FaChevronDown } from 'react-icons/fa6';
import { FaHeart } from 'react-icons/fa';
import { useAuthContext } from 'context/AuthContext';
import { useDismissible, useBodyScrollLock } from 'hooks/useDismissible';
import { prefetchRoute } from 'routes/routePrefetch';

const NAV_LINKS = [
  { to: ROUTES_ENUMS.HOME, label: 'Home', prefetch: prefetchRoute.home },
  { to: ROUTES_ENUMS.ABOUT_US, label: 'About Us', prefetch: prefetchRoute.aboutUs },
  { to: ROUTES_ENUMS.CATEGORIES, label: 'Categories', prefetch: prefetchRoute.categories },
  { to: ROUTES_ENUMS.TOP_PACKAGES, label: 'Top Packages', prefetch: prefetchRoute.topPackages },
  { to: ROUTES_ENUMS.CONTACT_US, label: 'Contact Us', prefetch: prefetchRoute.contactUs },
  { to: ROUTES_ENUMS.MY_WISHLIST, label: 'Wishlist', icon: FaHeart, prefetch: prefetchRoute.myWishlist },
];

const NavLink = ({ to, label, icon: Icon, onClick, prefetch, className = '' }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  const warmRoute = () => {
    if (typeof prefetch === 'function' && !isActive) {
      prefetch();
    }
  };

  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={warmRoute}
      onFocus={warmRoute}
      className={`self-stretch my-auto pb-[1px] border-b-2 transition-colors duration-200 flex items-center gap-1
        ${isActive ? 'border-orange-500 text-orange-400' : 'border-transparent hover:border-white hover:text-white'}
        ${className}`}
    >
      {Icon && <Icon className="text-sm" aria-hidden />}
      {label}
    </Link>
  );
};

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  onClick: PropTypes.func,
  prefetch: PropTypes.func,
  className: PropTypes.string,
};

const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, toggleRegister, handleLogout } = useAuthContext();

  const toggleSearch = () => setIsSearchOpen((open) => !open);
  const closeSearch = () => setIsSearchOpen(false);
  const toggleDropdown = () => setIsDropdownOpen((open) => !open);
  const closeDropdown = () => setIsDropdownOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((open) => !open);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  useDismissible(dropdownRef, isDropdownOpen, closeDropdown);
  useBodyScrollLock(isMobileMenuOpen);

  return (
    <header className="sticky top-0 z-[999] flex flex-col justify-center py-4 w-full text-sm text-white bg-black/70 backdrop-blur-md border-b border-white/10 max-md:max-w-full">
      <div className="flex flex-wrap gap-5 justify-between items-center px-5 w-full max-md:px-4">
        <Link to="/" className="shrink-0 focus-visible:rounded-lg" aria-label="RL Tours home">
          <img src="/assets/images/logo.png" className="w-[200px] md:w-[250px] h-auto" alt="RL Tours and Travels" />
        </Link>

        <button
          type="button"
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-white/30 text-xl hover:bg-white/10 transition-colors"
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <nav
          className="hidden md:flex flex-wrap gap-3 justify-center items-center self-stretch px-3 py-2 border border-white/25 min-w-[200px] rounded-[50px] bg-black/20"
          aria-label="Main navigation"
        >
          {NAV_LINKS.map(({ to, label, icon, prefetch }) => (
            <NavLink key={to} to={to} label={label} icon={icon} prefetch={prefetch} />
          ))}
          <button
            type="button"
            onClick={toggleSearch}
            className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Search destinations"
          >
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/a0ab420c4f2f81363d652f13e500826799989ba5efb048a039c31622f7d2c50e?placeholderIfAbsent=true&apiKey=234211a34b5d423b98c792be28684ce9"
              className="object-contain w-5 h-5"
              alt=""
            />
          </button>
        </nav>

        <div className="relative hidden md:block" ref={dropdownRef}>
          {user ? (
            <button
              type="button"
              className="text-white font-bold flex items-center gap-3 hover:text-orange-400 transition-colors rounded-lg px-1 py-1"
              onClick={toggleDropdown}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              {user.profileImage ? (
                <div className="relative w-10 h-10 rounded-full bg-[#373737] overflow-hidden">
                  <img src={user.profileImage} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <FaRegUserCircle className="text-3xl" aria-hidden />
              )}
              <span className="text-left text-sm leading-tight">
                Hi,<br />
                {user.name || user.mobileNo}
              </span>
              <FaChevronDown className={`text-sm transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} aria-hidden />
            </button>
          ) : (
            <button type="button" className="btn-ghost" onClick={toggleRegister}>
              Login / Signup
            </button>
          )}

          {isDropdownOpen && user && (
            <div
              className="absolute right-0 mt-2 w-52 bg-zinc-900/95 text-white rounded-xl shadow-xl border border-white/10 overflow-hidden"
              role="menu"
            >
              <Link to="/my-bookings" className="flex items-center px-4 py-3 hover:bg-white/10 transition-colors" role="menuitem" onClick={closeDropdown}>
                <RiFileList2Line className="text-xl mr-2" aria-hidden /> My Bookings
              </Link>
              <Link to="/my-wishlist" className="flex items-center px-4 py-3 hover:bg-white/10 transition-colors" role="menuitem" onClick={closeDropdown}>
                <FaHeart className="text-xl mr-2" aria-hidden /> Wishlist
              </Link>
              <Link to="/my-profile" className="flex items-center px-4 py-3 hover:bg-white/10 transition-colors" role="menuitem" onClick={closeDropdown}>
                <LuUserRound className="text-xl mr-2" aria-hidden /> Profile
              </Link>
              <Link to="/faq" className="flex items-center px-4 py-3 hover:bg-white/10 transition-colors" role="menuitem" onClick={closeDropdown}>
                <FaRegQuestionCircle className="text-xl mr-2" aria-hidden /> FAQ
              </Link>
              <button type="button" className="flex items-center w-full text-left px-4 py-3 text-red-400 hover:bg-white/10 transition-colors border-t border-white/10" role="menuitem" onClick={() => { handleLogout(); closeDropdown(); }}>
                <LuLogOut className="text-xl mr-2" aria-hidden /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {isMobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/60 z-[998] md:hidden"
          aria-label="Close menu overlay"
          onClick={closeMobileMenu}
        />
      )}

      <nav
        className={`
          flex flex-col md:hidden bg-zinc-900/98 border-t border-white/10 px-5 py-4 w-full
          absolute left-0 z-[999] shadow-2xl
          transition-all duration-300 ease-out
          ${isMobileMenuOpen ? 'top-full opacity-100 translate-y-0' : 'top-full opacity-0 -translate-y-2 pointer-events-none'}
        `}
        aria-label="Mobile navigation"
        aria-hidden={!isMobileMenuOpen}
      >
        {NAV_LINKS.map(({ to, label, icon, prefetch }) => (
          <NavLink
            key={to}
            to={to}
            label={label}
            icon={icon}
            prefetch={prefetch}
            onClick={closeMobileMenu}
            className="py-3 border-b border-white/10 w-full"
          />
        ))}
        <button
          type="button"
          className="flex items-center gap-2 py-3 text-left w-full hover:text-orange-400 transition-colors"
          onClick={() => { toggleSearch(); closeMobileMenu(); }}
        >
          Search destinations
        </button>
        <div className="mt-3 pt-3 border-t border-white/10">
          {user ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                {user.profileImage ? (
                  <img src={user.profileImage} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <FaRegUserCircle className="text-2xl" aria-hidden />
                )}
                <span className="font-semibold">{user.name || user.mobileNo}</span>
              </div>
              <Link to="/my-bookings" className="flex items-center py-2.5 hover:text-orange-400" onClick={closeMobileMenu}>
                <RiFileList2Line className="mr-2" /> My Bookings
              </Link>
              <Link to="/my-profile" className="flex items-center py-2.5 hover:text-orange-400" onClick={closeMobileMenu}>
                <LuUserRound className="mr-2" /> Profile
              </Link>
              <Link to="/faq" className="flex items-center py-2.5 hover:text-orange-400" onClick={closeMobileMenu}>
                <FaRegQuestionCircle className="mr-2" /> FAQ
              </Link>
              <button type="button" className="flex items-center w-full py-2.5 text-red-400" onClick={() => { handleLogout(); closeMobileMenu(); }}>
                <LuLogOut className="mr-2" /> Logout
              </button>
            </>
          ) : (
            <button type="button" className="btn-ghost w-full mt-2" onClick={() => { toggleRegister(); closeMobileMenu(); }}>
              Login / Signup
            </button>
          )}
        </div>
      </nav>

      <SearchDestination isOpen={isSearchOpen} onClose={closeSearch} />
    </header>
  );
};

export default Header;
